"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { stegaClean } from "@sanity/client/stega";

import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

export default function NavBar({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const items = data?.navItems;
  const [menuOpen, setMenuOpen] = React.useState(false);

  const jump = React.useCallback((anchor: string) => {
    const target = document.querySelector(anchor);
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth" });
    return true;
  }, []);

  const onJump = (event: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    if (!anchor) return;
    if (jump(anchor)) event.preventDefault();
    setMenuOpen(false);
  };

  // the sheet is a scroll trap on a page this long, so hold the body while it is open
  React.useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const links = items?.map((item, index) => {
    const anchor = stegaClean(item?.anchor) || "";
    return (
      <a
        key={index}
        href={anchor || "#"}
        onClick={(event) => onJump(event, anchor)}
        className="text-black uppercase transition-opacity duration-200 hover:opacity-100 font-geist leading-[1.3]">
        {item?.label}
      </a>
    );
  });

  return (
    <div data-ph-section="header" className={cn("fixed inset-x-0 top-0 z-50 bg-[#F3F2F1]", className)}>
      {/* 7rem is the mobile Figma's 56px row, 8rem the desktop 64px; the 2px rule below adds the rest */}
      <div className="mx-auto flex h-[7rem] w-[min(240rem,100%)] items-center justify-between px-[2rem] sm:px-[3rem] lg:h-[8rem] lg:px-[12.5rem]">
        {/*
          The monogram blends against the bar's own fill, which is painted first
          inside this fixed element's stacking context — so it reads near-black on
          the light bar and would invert if the bar ever went dark.
        */}
        {data?.navMonogram && (
          <a
            href="#about"
            onClick={(event) => onJump(event, "#about")}
            aria-label={data.navMonogram}
            className="leading-none text-white uppercase shrink-0 font-funnel text-[4.47rem] font-[800] tracking-[0.02em] mix-blend-difference lg:text-[4.5rem]">
            {data.navMonogram}
          </a>
        )}

        <nav className="hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:block">
          <div className="flex items-center gap-[7rem] text-[2rem] opacity-70 [&_a]:hover:opacity-100">{links}</div>
        </nav>

        <div className="flex items-center shrink-0 gap-[2rem]">
          {data?.navCTA && (
            <CTACornerButton
              className="hidden w-auto min-w-0 lg:inline-block"
              data={data.navCTA}
            />
          )}

          {/*
            below lg the five links live in a sheet rather than a scrolling strip.
            The trigger sits in the Figma's beveled tile: 1px grey hairline, 2px white
            ring, page-coloured face inside, with the bars crossing at its centre.
          */}
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
            className="lg:hidden h-[6rem] w-[7.5rem] rounded-[1rem] bg-[#D3D3D2] p-[0.125rem]">
            <span className="block w-full h-full rounded-[0.875rem] bg-white p-[0.25rem]">
              <span className="flex relative justify-center items-center w-full h-full rounded-[0.75rem] bg-[#F3F2F1] opacity-70">
                <span
                  className={cn(
                    "absolute h-[0.25rem] w-[3.75rem] bg-black transition-transform duration-300",
                    menuOpen ? "rotate-45" : "-translate-y-[0.5rem]",
                  )}
                />
                <span
                  className={cn(
                    "absolute h-[0.25rem] w-[3.75rem] bg-black transition-transform duration-300",
                    menuOpen ? "-rotate-45" : "translate-y-[0.5rem]",
                  )}
                />
              </span>
            </span>
          </button>
        </div>
      </div>

      <div className="w-full h-[2px]">
        <div className="w-full h-[0.5px] bg-[#202223]/[.15]" />
        <div className="w-full bg-white h-[1.5px]" />
      </div>

      {/* mobile sheet */}
      <div
        className={cn(
          "fixed inset-x-0 top-[7.25rem] bottom-0 bg-[#F3F2F1] transition-[opacity,transform] duration-300 lg:hidden",
          menuOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-[2rem] opacity-0",
        )}
        aria-hidden={!menuOpen}>
        <div className="flex flex-col w-full h-full px-[2rem] pt-[6.375rem] pb-[2.375rem]">
          <nav className="flex flex-col gap-[2.25rem] text-[2.25rem] opacity-70">
            {items?.map((item, index) => {
              const anchor = stegaClean(item?.anchor) || "";
              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    /* two-tone rule: 1px of #202223/15 over 1.5px of white, per Figma */
                    <div className="w-full h-[2.5px] shrink-0">
                      <div className="w-full h-[1px] bg-[#202223]/[.15]" />
                      <div className="w-full bg-white h-[1.5px]" />
                    </div>
                  )}
                  <a
                    href={anchor || "#"}
                    onClick={(event) => onJump(event, anchor)}
                    className="text-black uppercase font-geist leading-[1.3]">
                    {item?.label}
                  </a>
                </React.Fragment>
              );
            })}
          </nav>

          {data?.navCTA && (
            /*
              Figma's mobile bar is the corner button at 0.857x: 48px tall, 15.43px
              label, 13.71px corner cut. Scaled from here rather than in the shared
              component so every other page keeps the full-size bar.
            */
            <CTACornerButton
              className={cn(
                "mt-auto mx-[0.375rem] !w-[calc(100%-0.75rem)] !min-w-0",
                "[&_.corner-button]:min-h-[6rem] [&_.corner-button]:gap-[1.714rem] [&_.corner-button]:px-[3.429rem] [&_.corner-button]:text-[1.929rem]",
                "[&_.corner-button]:[clip-path:polygon(0_0,100%_0,100%_calc(100%-1.714rem),calc(100%-1.714rem)_100%,0_100%)]",
                "[&_.corner-button-divider]:h-[3.429rem]",
                "[&_.corner-button-icon-initial]:h-[2.571rem] [&_.corner-button-icon-initial]:w-[2.571rem]",
                "[&_.corner-button-icon-hover]:h-[2.571rem] [&_.corner-button-icon-hover]:w-[2.571rem]",
              )}
              data={data.navCTA}
            />
          )}
        </div>
      </div>
    </div>
  );
}
